import React, { useState, useEffect } from "react";
import "./PostComposer.css";
import { db, storage, auth } from "../firebaseConfig";
import { addDoc, collection, serverTimestamp, updateDoc, getDoc, doc, setDoc, arrayUnion,} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL} from "firebase/storage";
import { toast } from "react-toastify";
import { getNewPostExpiresAt } from "../utils/postLifecycle";


const PostComposer = ({ onSubmit }) => {
  const [text, setText] = useState("");
  const [topic, setTopic] = useState("");
  const [images, setImages] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [isPosting, setIsPosting] = useState(false);

  const sanitize = (v) => {
    try {
      let s = String(v ?? "");
      s = s.replace(/[\r\n]+/g, " ");
      s = s.replace(/[\u2012-\u2015]/g, " - ");
      s = s.replace(/[^\x20-\x7E]+/g, "");
      s = s.replace(/\s{2,}/g, " ").trim();
      return s;
    } catch {
      return v;
    }
  };

  const nameInitialLast = (p) => {
    if (!p) return "Investigator";
    if (p.firstName && p.lastName) return `${p.firstName[0]}. ${p.lastName}`;
    if (p.firstInitialLastName)
      return p.firstInitialLastName.includes(".")
        ? p.firstInitialLastName.replace(".", ". ")
        : p.firstInitialLastName;
    return p.displayId || "Investigator";
  };

  const formatLocation = (p) => {
    const profile = p || {};
    return [profile.country, profile.stateProvince || profile.state, profile.city]
      .map((part) => sanitize(part))
      .filter(Boolean)
      .join(", ");
  };

  // Detect screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load current user's profile to render privacy labels
  useEffect(() => {
    const run = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setProfileData(snap.data());
    };
    run();
  }, []);



const handleSubmit = async (e) => {
  e.preventDefault();
  if (isPosting || !text.trim()) return;

  try {
    setIsPosting(true);
    const user = auth.currentUser;
    if (!user) {
      toast.error("Please log in to post.");
      return;
    }

   const profileRef = doc(db, "users", user.uid);
const profileSnapshot = await getDoc(profileRef);
const profile = profileSnapshot.data();

const postData = {
  userId: user.uid,
  userName: nameInitialLast(profile),
  authorDisplayName: nameInitialLast(profile),
  country: profile?.country || "",
  stateProvince: profile?.stateProvince || profile?.state || "",
  state: profile?.stateProvince || profile?.state || "",
  city: profile?.city || "",
  authorLocation: formatLocation(profile),
  text: text.trim(),
  topic: topic.trim().toLowerCase() || null,
  linkUrl: linkUrl.trim() || null,
  privacyTier: "real",
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
  likes: 0,
  views: 0,
  commentCount: 0,
  isHidden: false,
  authorDeleted: false,
  containsModeratedContent: false,
  pinned: false,
  images: [],
  imagePaths: [],
  expiresAt: getNewPostExpiresAt(),
};


    //  Upload up to 3 images if provided
    if (images && images.length > 0) {
      const toUpload = images.slice(0, 3);
      const urls = [];
      const paths = [];
      for (const file of toUpload) {
        const imagePath = `posts/${user.uid}/${Date.now()}_${file.name}`;
        const imgRef = ref(storage, imagePath);
        await uploadBytes(imgRef, file);
        const imgUrl = await getDownloadURL(imgRef);
        urls.push(imgUrl);
        paths.push(imagePath);
      }
      postData.images = urls;
      postData.imagePaths = paths;
    }

    //  Add post to Firestore
    const docRef = await addDoc(collection(db, "posts"), postData);

    //  Trigger topic list update (metadata doc)
   if (topic.trim()) {
  const metaRef = doc(db, "metadata", "topics");
  const newTopic = topic.trim().toLowerCase();

  try {
    const snap = await getDoc(metaRef);
    if (snap.exists()) {
      await updateDoc(metaRef, {
        latestTopics: arrayUnion(newTopic),
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(metaRef, {
        latestTopics: [newTopic],
        updatedAt: serverTimestamp(),
      });
    }
  } catch (err) {
    console.warn("Topic update failed:", err.message);
  }
}

    //  Reset UI
    setText("");
    setTopic("");
    setImages([]);
    setLinkUrl("");
    toast.success("Post shared successfully!");

    if (onSubmit) onSubmit(); 

  } catch (err) {
    console.error("Error posting:", err);
    toast.error("Failed to share post. Try again.");
  } finally {
    setIsPosting(false);
  }
};


  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImages((prev) => [...prev, ...files].slice(0, 3));
  };

  const identityPreview = `${sanitize(nameInitialLast(profileData))}${
    formatLocation(profileData) ? ` (${formatLocation(profileData)})` : ""
  }`;

  const composerBody = (
    <form className="composer-form" onSubmit={handleSubmit}>
      <textarea
        placeholder="Share your thoughts..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        required
      />

      <div className="composer-options">
      <input
  type="text"
  placeholder="Topic (optional, max 40 chars)"
  value={topic}
  maxLength={41}
  onChange={(e) => setTopic(e.target.value)}
/>

        <input
          type="url"
          placeholder="Video or article link (optional)"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
        />

        <label className="image-upload">
  Add Image(s) (max 3)
  <input
    type="file"
    accept="image/*"
    multiple
    onChange={handleImageChange}
  />
</label>

        <div className="identity-preview" title="Posts use your verified ColdWire profile">
          Posting as {identityPreview}
        </div>
      </div>

 {images && images.length > 0 && (
          <>
            
            <div className="composer-previews">
              {images.map((f, idx) => {
                const url = URL.createObjectURL(f);
                return (
                  <div key={idx} className="thumb">
                    <div className="thumb-image">
                      <img src={url} alt={`Selected ${idx + 1}`} onLoad={() => URL.revokeObjectURL(url)} />
                    </div>
                    <button
                      type="button"
                      className="remove"
                      onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                      aria-label="Remove image"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

      <button
        type="submit"
        className="post-btn"
        disabled={isPosting || !text.trim()}
        title={!text.trim() ? "Post text required" : "Post"}
      >
        {isPosting ? "Posting..." : "Post"}
      </button>
    </form>
  );
return <div className="composer-inline">{composerBody}</div>;
};

export default PostComposer;
